import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Search, BadgeCheck, Ban } from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";

function UserRow({ u, onToggle }) {
  return (
    <div data-testid={`admin-user-row-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-zinc-100">{u.name || "Zonder naam"}</span>
          <VerifiedBadge show={u.verified} testid={`admin-user-verified-${u.id}`} />
          {u.role === "admin" && <span className="rounded-full bg-rose-950/60 border border-rose-800/50 px-2 py-0.5 text-[10px] uppercase text-rose-300">Beheerder</span>}
          {u.banned && <span className="flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] uppercase text-zinc-400"><Ban className="h-3 w-3" /> Geblokkeerd</span>}
        </div>
        <div className="text-xs text-zinc-500 truncate">{u.email} · {u.products} producten · {u.services} diensten</div>
        {u.created_at && (
          <div data-testid={`admin-user-created-${u.id}`} className="text-[11px] text-zinc-600">
            Aangemeld op {new Date(u.created_at).toLocaleString("nl-NL", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>
      <button data-testid={`admin-verify-toggle-${u.id}`} onClick={() => onToggle(u)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${u.verified ? "border-zinc-700 text-zinc-300 hover:bg-zinc-800" : "border-sky-600 bg-sky-600 text-white hover:bg-sky-500"}`}>
        <BadgeCheck className="h-3.5 w-3.5" /> {u.verified ? "Verificatie intrekken" : "Verifiëren"}
      </button>
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/users", { params: { q } }).then(({ data }) => setUsers(data)).catch(() => toast.error("Kon gebruikers niet laden"));
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const toggle = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/verify`, null, { params: { verified: !u.verified } });
      toast.success(u.verified ? `Verificatie van ${u.name || u.email} ingetrokken` : `${u.name || u.email} is nu geverifieerd`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, verified: !u.verified } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kon status niet wijzigen");
    }
  };

  return (
    <div data-testid="admin-users-page" className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-zinc-100">Gebruikers</h1>
      <p data-testid="admin-users-count" className="mt-2 text-sm text-zinc-300">{users.length} {users.length === 1 ? "account" : "accounts"}{q ? " gevonden" : " — nieuwste bovenaan"}</p>
      <p className="mt-1 text-sm text-zinc-500">Geef gecontroleerde verkopers en dienstverleners de badge <span className="text-sky-300">Geverifieerd</span>.</p>
      <div className="relative mt-6 mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of e-mail..."
          className="w-full rounded-full border border-zinc-800 bg-zinc-900/80 pl-11 pr-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none" />
      </div>
      <div className="space-y-3">
        {users.map((u) => <UserRow key={u.id} u={u} onToggle={toggle} />)}
        {!users.length && <div data-testid="admin-users-empty" className="py-12 text-center text-sm text-zinc-500">Geen gebruikers gevonden</div>}
      </div>
    </div>
  );
}
